import { useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { VOICIFY_SPARK_DEPLOYMENT_STATE } from "../constants/Keys";
import * as voicifyApi from '../api';
import { MultipleTemplateSyncsStatus } from "../state/containers/AppliedApplicationTemplateContainer";
import IResult from "../models/result/IResult";
import AppliedTemplateSyncModel from "../models/templating/api/AppliedTemplateSyncModel";

export type TrainingState = "training" | "trained" | "error";

export interface SparkDeploymentEntry {
    appliedTemplateSyncId?: string;
    orgId: string;
    syncStage?: "Processing" | "Queued" | "Complete" | "Error";
    timestamp?: number;
    pollingFailures?: number;
    error?: string;
}

interface SparkDeploymentState {
    [appId: string]: SparkDeploymentEntry;
}

const MAX_POLLING_FAILURES = 3;
const POLLING_INTERVAL_MS = 7500;

export const getSparkTrainingState = (entry?: SparkDeploymentEntry): TrainingState => {
    if (!entry) return "trained";
    // Terminal errors remain errors after the tracking ID has been cleared.
    if (entry.syncStage === "Error") return "error";
    if (entry.syncStage === "Complete") return "trained";
    if (entry.appliedTemplateSyncId) return "training";
    if (entry.syncStage === "Queued" || entry.syncStage === "Processing") return "error";
    return "trained";
};

export const applySparkSyncResult = (entry: SparkDeploymentEntry, result?: IResult<AppliedTemplateSyncModel>): SparkDeploymentEntry => {
    const stage = result?.data?.stage;
    if (result?.resultType === "Ok" && (stage === "Complete" || stage === "Error" || stage === "Queued" || stage === "Processing")) {
        return {
            ...entry,
            syncStage: stage === "Complete" && !!result.data.error ? "Error" : stage,
            appliedTemplateSyncId: stage === "Complete" || stage === "Error" ? undefined : entry.appliedTemplateSyncId,
            pollingFailures: 0,
            error: stage === "Error" || (stage === "Complete" && !!result.data.error)
                ? (result.data.error || "Publishing failed. Please try again or contact support.")
                : undefined
        };
    }
    const failures = (entry.pollingFailures || 0) + 1;
    const permanent = result?.resultType === "NotFound" || result?.resultType === "Unauthorized" || result?.resultType === "PermissionDenied";
    const failed = permanent || failures >= MAX_POLLING_FAILURES;
    return {
        ...entry,
        // A failed status read is never evidence of a successful deployment.
        syncStage: failed ? "Error" : entry.syncStage,
        pollingFailures: failures,
        error: "Could not confirm publishing status. Please check again or contact support."
    };
};

function useSparkDeploymentContainer() {
    const [, setCurrentOrgId] = useState<string>("");
    const [deploymentState, setDeploymentState] = useState<SparkDeploymentState>(null);
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        const storedJson = localStorage.getItem(VOICIFY_SPARK_DEPLOYMENT_STATE);
        const stored: SparkDeploymentState = storedJson ? JSON.parse(storedJson) : {};
        const cleaned: SparkDeploymentState = {};
        const now = Date.now();
        Object.entries(stored).forEach(([appId, entry]) => {
            if (!entry.timestamp || now - entry.timestamp < 24 * 60 * 60 * 1000) cleaned[appId] = entry;
        });
        setDeploymentState(cleaned);
    }, []);

    useEffect(() => {
        if (deploymentState) localStorage.setItem(VOICIFY_SPARK_DEPLOYMENT_STATE, JSON.stringify(deploymentState));
    }, [deploymentState]);

    useEffect(() => {
        if (!polling) return;
        const appIds = Object.keys(deploymentState || {}).filter(appId => getSparkTrainingState(deploymentState[appId]) === "training");
        if (!appIds.length) {
            setPolling(false);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            const updates = await Promise.all(appIds.map(async appId => {
                const entry = deploymentState[appId];
                let result: IResult<AppliedTemplateSyncModel>;
                try {
                    result = await voicifyApi.getAppliedTemplateSync(entry.appliedTemplateSyncId);
                } catch {
                    // Network exceptions follow the same bounded retry path as returned failures.
                }
                return { appId, entry, updated: applySparkSyncResult(entry, result) };
            }));
            if (cancelled) return;
            setDeploymentState(previous => {
                const next = { ...previous };
                updates.forEach(({ appId, entry, updated }) => {
                    // A response from an older publish must not overwrite a newer publish.
                    if (previous[appId] === entry) next[appId] = updated;
                });
                return next;
            });
        }, POLLING_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [polling, deploymentState]);

    const pollForApplication = async (appId: string, orgId: string, appliedTemplateSyncId?: string) => {
        if (!appliedTemplateSyncId) return;
        setDeploymentState(previous => ({
            ...previous,
            [appId]: { appliedTemplateSyncId, orgId, syncStage: "Queued", timestamp: Date.now(), pollingFailures: 0 }
        }));
        setPolling(true);
    };

    const getAppDeploymentState = (appId: string, containerState?: {
        isWaitingForTemplateSync?: boolean;
        multipleTemplateSyncsStatus?: MultipleTemplateSyncsStatus;
    }): TrainingState => {
        if (containerState?.isWaitingForTemplateSync || containerState?.multipleTemplateSyncsStatus === "syncing") return "training";
        if (containerState?.multipleTemplateSyncsStatus === "error") return "error";
        if (getSparkTrainingState(deploymentState?.[appId]) === "error") return "error";
        if (containerState?.multipleTemplateSyncsStatus === "success") return "trained";
        return getSparkTrainingState(deploymentState?.[appId]);
    };

    return { setCurrentOrgId, pollForApplication, getAppDeploymentState, deploymentState };
}

const SparkDeploymentContainer = createContainer(useSparkDeploymentContainer);
export default SparkDeploymentContainer;
